import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Copy, Ban, Send, Mail } from "lucide-react";

export default function AdminInvitations() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", expires_at: "", invitee_name: "", invitee_email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/invitations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        label: form.label || undefined,
        expires_at: form.expires_at || undefined,
        invitee_name: form.invitee_name?.trim() || undefined,
        invitee_email: form.invitee_email?.trim().toLowerCase() || undefined,
      };
      await api.post("/admin/invitations", body);
      toast.success(body.invitee_email
        ? `Convite gerado e enviado para ${body.invitee_email}`
        : "Convite criado (uso único)");
      setForm({ label: "", expires_at: "", invitee_name: "", invitee_email: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revogar o convite ${inv.code}?`)) return;
    await api.delete(`/admin/invitations/${inv.id}`);
    load();
  };

  const copyLink = async (inv) => {
    const url = `${window.location.origin}/register?code=${inv.code}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link de convite copiado");
  };

  const resend = async (inv) => {
    try {
      await api.post(`/admin/invitations/${inv.id}/resend`);
      toast.success(`Convite reenviado para ${inv.invitee_email}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Acesso</div>
        <h1 className="font-heading text-5xl mt-2 flex items-center gap-3"><Ticket className="w-8 h-8 text-[var(--forest)]"/> Convites</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">Só quem tem código de convite pode criar conta. Cada código é <strong>único e de utilização única</strong> — assim que for consumido por uma inscrição, deixa de funcionar. Se indicar o email do convidado, enviamos automaticamente o link de registo já preenchido.</p>
      </div>

      <form onSubmit={add} className="card-surface p-6 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--ink-soft)]">Nome do convidado (opcional)</label>
            <input value={form.invitee_name} onChange={(e) => setForm({ ...form, invitee_name: e.target.value })} placeholder="Ex: Maria Silva" className={`mt-1 ${inputCls}`} data-testid="inv-invitee-name-input"/>
          </div>
          <div>
            <label className="text-xs text-[var(--ink-soft)]">Email do convidado (opcional — envia link automaticamente)</label>
            <input type="email" value={form.invitee_email} onChange={(e) => setForm({ ...form, invitee_email: e.target.value })} placeholder="maria@exemplo.pt" className={`mt-1 ${inputCls}`} data-testid="inv-invitee-email-input"/>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <input placeholder="Etiqueta interna (opcional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} data-testid="inv-label-input"/>
          <div>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={inputCls} data-testid="inv-expires-input" aria-label="Data de expiração (opcional)"/>
            <div className="text-[10px] text-[var(--ink-soft)] mt-1">Expira (aaaa-mm-dd) · opcional</div>
          </div>
          <button disabled={busy} className="pill-btn pill-primary" data-testid="inv-add-btn">
            {form.invitee_email ? <><Send className="w-4 h-4"/> {busy ? "…" : "Gerar e enviar"}</> : <><Plus className="w-4 h-4"/> {busy ? "…" : "Gerar convite"}</>}
          </button>
        </div>
        <div className="text-[10px] text-[var(--ink-soft)]">O código do convite é gerado automaticamente pelo sistema (8 caracteres únicos).</div>
      </form>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Convidado</th>
              <th className="px-4 py-3">Estado do código</th>
              <th className="px-4 py-3">Expira</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => {
              const used = (inv.uses_count || 0) >= 1;
              const revoked = inv.revoked;
              return (
                <tr key={inv.id} className="border-t border-[var(--border)]" data-testid={`invitation-row-${inv.code}`}>
                  <td className="px-4 py-3 font-mono font-semibold tracking-wider">{inv.code}</td>
                  <td className="px-4 py-3">
                    {inv.invitee_name || inv.invitee_email
                      ? <div>
                          <div className="font-medium">{inv.invitee_name || "—"}</div>
                          {inv.invitee_email && <div className="text-xs text-[var(--ink-soft)]">{inv.invitee_email}</div>}
                        </div>
                      : <span className="text-[var(--ink-soft)]">{inv.label || "—"}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {revoked
                      ? <span className="chip chip-terra">Revogado</span>
                      : used
                        ? <span className="chip chip-neutral" title={inv.last_used_at ? `Consumido em ${new Date(inv.last_used_at).toLocaleString("pt-PT")}` : ""}>Consumido</span>
                        : <span className="chip chip-green">Disponível</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("pt-PT") : "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {inv.email_sent_at
                      ? <span className="chip chip-green" title={new Date(inv.email_sent_at).toLocaleString("pt-PT")}><Mail className="w-3 h-3 inline mr-1"/> Enviado</span>
                      : inv.invitee_email
                        ? <span className="chip chip-neutral">Pendente</span>
                        : <span className="text-[var(--ink-soft)]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {!used && !revoked && inv.invitee_email && (
                        <button onClick={() => resend(inv)} className="pill-btn pill-ghost !py-1 !px-2" title="Reenviar email de convite" data-testid={`resend-inv-${inv.code}`}>
                          <Send className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {!used && !revoked && (
                        <button onClick={() => copyLink(inv)} className="pill-btn pill-ghost !py-1 !px-2" title="Copiar link" data-testid={`copy-inv-${inv.code}`}>
                          <Copy className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {!revoked && !used && (
                        <button onClick={() => revoke(inv)} className="pill-btn pill-ghost !py-1 !px-2 !text-red-600" title="Revogar" data-testid={`revoke-inv-${inv.code}`}>
                          <Ban className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-soft)]">Sem convites. Gere o primeiro acima.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
